import React, { useState, useEffect } from "react";
import StyledHeadingText from "../../components/Fonts/StyledHeadingText";
import MapComponent from "../../components/MapComponent";
import { formatDate, formatDateTime } from "../../utils/FormatDateTime";
import { patchFarmLatLng, patchSatelliteFarm, sendSatelliteReport } from "../../services/satelliteFarm";
import Button from "../../components/buttons/Button";
import { toast } from "react-toastify";
import { confirmDialog } from "../../utils/confirmDialog";
import { getUserId } from "../../helpers/local_storage_helpers";
import { FiEdit2, FiCopy } from "react-icons/fi";
import SatelliteDataCharts from "./SatelliteDataCharts";
import { Link } from "react-router-dom";

function SatelliteEnquiryDetails({ item, onAnyChange }) {
    const [isEditMode, setIsEditMode] = useState(false);
    const [editablePolygon, setEditablePolygon] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [originalPolygon, setOriginalPolygon] = useState([]);
    const [selectedLayer, setSelectedLayer] = useState("ndvi");

    useEffect(() => {
        if (item.farm?.geo_tag) {
            try {
                const parsedGeoTag = JSON.parse(item.farm.geo_tag);
                if (parsedGeoTag.length > 0) {
                    setEditablePolygon(parsedGeoTag);
                    setOriginalPolygon(parsedGeoTag);
                }
            } catch (error) {
                console.error("Error parsing geo_tag:", error);
            }
        }
    }, [item.farm?.geo_tag]);

    const layers = [
        { id: "ndvi", name: "NDVI (Vegetation)" },
        { id: "evi", name: "EVI (Enhanced Vegetation)" },
        { id: "savi", name: "SAVI (Soil Adjusted)" },
        { id: "ndre", name: "NDRE (Red Edge)" },
        { id: "ndmi", name: "NDMI (Moisture)" },
        { id: "ndwi", name: "NDWI (Water)" },
        { id: "soc_vis", name: "SOC VIS (Soil Organic Carbon)" },
        { id: "soc_swir", name: "SOC SWIR (Soil Organic Carbon)" },
        { id: "lai", name: "LAI (Leaf Area)" },
    ];

    const handlePolygonEdit = (newCoordinates) => {
        setEditablePolygon(newCoordinates);
    };

    const handleSave = async () => {
        if (!editablePolygon || editablePolygon.length === 0) {
            alert("No polygon data to save");
            return;
        }

        if (editablePolygon.length < 3) {
            alert("Polygon must have at least 3 points");
            return;
        }

        setIsSaving(true);
        try {
            const updateData = {
                geo_tag: JSON.stringify(editablePolygon)
            };

            const response = await patchFarmLatLng(item.farm.id, updateData);

            if (response.status === 200) {
                setIsEditMode(false);
                setOriginalPolygon([...editablePolygon]);
                toast.success("Farm updated successfully");
                onAnyChange();
            } else {
                toast.error("Unable to update farm");
            }


        } catch (error) {
            console.error("Error saving polygon:", error);
            alert("Failed to save polygon area. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditablePolygon([...originalPolygon]);
        setIsEditMode(false);
    };

    const toggleEditMode = () => {
        if (isEditMode) {
            handleCancel();
        } else {
            setIsEditMode(true);
        }
    };

    const hasChanges = () => {
        if (editablePolygon.length !== originalPolygon.length) return true;
        return editablePolygon.some((point, index) =>
            point.lat !== originalPolygon[index]?.lat ||
            point.lng !== originalPolygon[index]?.lng
        );
    };

    const handleEditNote = async () => {
        const result = await confirmDialog(
            "Update Sales Team Notes",
            "Edit Note",
            {
                showInput: true,
                inputPlaceholder: "Enter notes...",
                confirmText: "Update",
                inputValue: item.sales_team_notes || ""
            }
        );

        if (result !== false) {
            try {
                const response = await patchSatelliteFarm(item.id, {
                    sales_team_notes: result.notes,
                    sales_team: getUserId()
                });
                if (response.status === 200) {
                    toast.success("Note updated successfully");
                    onAnyChange();
                } else {
                    toast.error("Unable to update note");
                }
            } catch (error) {
                console.error("Error updating note:", error);
                toast.error("An error occurred while updating the note");
            }
        }
    };

    // Extract satellite data for selection
    const satelliteData = item.farm_satellite_data?.[0];
    const currentTileUrl = satelliteData?.[`${selectedLayer}_observation`]?.tile_url;



    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    return (
        <>
            {/* Header Section */}
            <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4 mb-6 border border-gray-100 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                            <h2 className="text-xl font-bold text-gray-800">Satellite Enquiry Details</h2>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{item.farm?.name || "Farm"}</span>
                                <span>•</span>
                                <span>{item.user_name}</span>
                                <span>•</span>
                                <div className="flex items-center gap-1 cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(item.id)}>
                                    <span className="font-mono">{item.id}</span>
                                    <FiCopy size={12} />
                                </div>
                            </div>
                        </div>

                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Layer Selector */}
                        {item.has_satellite_data && (
                            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Layer</span>
                                <select
                                    value={selectedLayer}
                                    onChange={(e) => setSelectedLayer(e.target.value)}
                                    className="bg-transparent text-sm font-medium focus:outline-none cursor-pointer text-gray-700 hover:text-primary transition-colors"
                                >
                                    {layers.map(layer => (
                                        <option key={layer.id} value={layer.id}>{layer.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Edit Actions */}
                        <div className="flex gap-2">
                            <Button
                                onClick={toggleEditMode}
                                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${isEditMode
                                    ? 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                    }`}
                            >
                                {isEditMode ? 'Cancel Edit' : 'Edit Farm Area'}
                            </Button>

                            {isEditMode && hasChanges() && (
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm disabled:opacity-50 shadow-sm transition-all"
                                >
                                    {isSaving ? 'Saving...' : 'Save Area'}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Map Section */}
                <div className="w-full rounded-lg aspect-square border border-black relative overflow-hidden group">
                    {isEditMode && (
                        <div className="absolute top-2 left-2 z-[1001] bg-blue-600 text-white px-3 py-1 rounded-md text-sm font-medium shadow-lg">
                            Editing Mode
                        </div>
                    )}
                    {isEditMode && (
                        <div className="absolute top-2 right-2 z-[1001] bg-gray-800 text-white px-3 py-1 rounded-md text-sm shadow-lg">
                            Vertices: {editablePolygon.length}
                        </div>
                    )}

                    {item.has_satellite_data && currentTileUrl && (
                        <div className="absolute bottom-4 left-4 z-[1001] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-xl  flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full animate-pulse bg-green-500"></div>
                                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">
                                    Satellite Overlay: {layers.find(l => l.id === selectedLayer)?.name}
                                </span>
                            </div>
                        </div>
                    )}

                    {
                        editablePolygon.length > 0 ?
                            <MapComponent
                                markersList={[]}
                                mapLocation={[editablePolygon[0].lat, editablePolygon[0].lng]}
                                zoomLevel={18}
                                areasList={[
                                    {
                                        title: item.farm?.name ?? "",
                                        area: editablePolygon,
                                    },
                                ]}
                                height='100%'
                                isEditMode={isEditMode}
                                onPolygonEdit={handlePolygonEdit}
                                satelliteTileUrl={currentTileUrl}
                            /> : <div className="flex items-center justify-center h-full text-gray-500">No polygon data available</div>
                    }
                </div>

                {/* Details Section */}
                <div className="w-full">
                    {item.farm_satellite_data && (
                        <SatelliteDataCharts
                            satelliteData={item.farm_satellite_data}
                            selectedLayer={selectedLayer}
                        />
                    )}

                    <StyledHeadingText text={"Enquiry Details"} size={"mt-4"} />
                    <div className='grid md:grid-cols-2 gap-y-4 gap-x-6 mt-4 text-sm'>
                        {/* User Info */}
                        <div className='flex flex-col p-3 bg-gray-50 rounded-lg'>
                            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>User Details</span>
                            <div className="mt-2 flex flex-col gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Name:</span>
                                    <span className="font-medium text-gray-900">{item.user_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Phone:</span>
                                    <span className="font-medium text-gray-900">{item.user_phone_number}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Address:</span>
                                    <span className="font-medium text-gray-900 text-right max-w-[60%] truncate" title={item.user_address}>{item.user_address || "-"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Farm Info */}
                        <div className='flex flex-col p-3 bg-gray-50 rounded-lg'>
                            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Farm Details</span>
                            <div className="mt-2 flex flex-col gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Name:</span>
                                    <span className="font-medium text-gray-900">{item.farm?.name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Size:</span>
                                    <span className="font-medium text-gray-900">{item.farm?.size} {item.farm?.area_unit}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Crop:</span>
                                    <span className="font-medium text-gray-900">{item.farm?.crop?.name || "-"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Irrigation:</span>
                                    <span className="font-medium text-gray-900">{item.farm?.irrigation_type || "-"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Soil:</span>
                                    <span className="font-medium text-gray-900">{item.farm?.soil_type || "-"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Subscription Info */}
                        <div className='flex flex-col p-3 bg-gray-50 rounded-lg'>
                            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Subscription & Plan</span>
                            <div className="mt-2 flex flex-col gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Plan:</span>
                                    <span className="font-medium text-gray-900 uppercase">{item.plan || "-"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Start Date:</span>
                                    <span className="font-medium text-gray-900">{formatDate(item.subscription_start_date)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">End Date:</span>
                                    <span className={`font-medium ${new Date(item.subscription_end_date) < new Date() ? 'text-red-600' : 'text-gray-900'}`}>
                                        {formatDate(item.subscription_end_date)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Status:</span>
                                    <span className={`font-bold ${item.farm_status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                                        {item.farm_status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Info */}
                        <div className='flex flex-col p-3 bg-gray-50 rounded-lg'>
                            <span className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>Payment Details</span>
                            <div className="mt-2 flex flex-col gap-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Amount:</span>
                                    <span className="font-medium text-gray-900">₹{item.paid_amount}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Status:</span>
                                    <span className={`font-bold ${item.payment_status === "Success" ? "text-green-600" : "text-red-600"}`}>
                                        {item.payment_status}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Mode:</span>
                                    <span className="font-medium text-gray-900">{item.payment_mode || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Transaction ID:</span>
                                    <div className="flex items-center gap-1 cursor-pointer hover:text-primary" onClick={() => copyToClipboard(item.transaction_id)}>
                                        <span className="font-mono text-xs max-w-[100px] truncate" title={item.transaction_id}>
                                            {item.transaction_id || "-"}
                                        </span>
                                        {item.transaction_id && <FiCopy size={10} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='flex flex-col mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100'>
                        <div className="flex items-center justify-between mb-2">
                            <span className='text-xs font-semibold text-blue-800 uppercase tracking-wider'>Sales Team Notes</span>
                            <div
                                className="flex items-center gap-1 text-xs text-blue-600 cursor-pointer hover:text-blue-800 font-medium"
                                onClick={handleEditNote}
                            >
                                <FiEdit2 size={12} />
                                <span>Edit Note</span>
                            </div>
                        </div>
                        <p className='text-sm text-gray-700 whitespace-pre-wrap'>{item.sales_team_notes || "No notes added."}</p>
                        <p className='text-xs text-gray-400 mt-2 italic'>Approved by: {item.sales_team || "Pending"}</p>
                    </div>

                    {item.farm?.image && (
                        <div className="mt-4">
                            <StyledHeadingText text={"Farm Image"} size={"my-2"} />
                            <img
                                src={item.farm?.image}
                                alt="Farm"
                                className="aspect-square w-32 h-32 object-cover rounded-lg border border-gray-200"
                            />
                        </div>
                    )}
                </div>
            </div>


        </>
    );
}

export default SatelliteEnquiryDetails;
